import { toSnakeCase } from '../utils/data-casing';
import {
  executeDeepLinkPayloadType,
  fetchAccessTokenCallbackType,
  fetchAnalyticsKeyCallbackType,
  fetchUserIdCallbackType,
  toggleWebviewLeaveDialogPayloadType,
} from './interface';

declare global {
  interface Window {
    webkit: any;
    autopass: any;
    setUserId: (id: string) => void; // only for ios
    responseAccessToken: (token: string) => void;
    responseAnalyticsKey: (token: string) => void;
  }
}

let targetProxy: any;
const proxyCallbackGroup: any = {};
const timeoutGroup: any = {};

if (typeof window !== 'undefined') {
  window.setUserId = (id: string) => {
    targetProxy.userId = id;
  };

  window.responseAccessToken = (token: string | Error) => {
    targetProxy.accessToken = token;
  };
  window.responseAnalyticsKey = (key: string) => {
    targetProxy.analyticsKey = key;
  };
}

abstract class Bridge {
  abstract toggleAutoRefresh(enable: boolean): void;
  abstract toggleWebviewLeaveDialog(
    payload: toggleWebviewLeaveDialogPayloadType,
  ): void;
  abstract fetchUserId(callback: fetchUserIdCallbackType): void;

  setProxy = (name: string, callback: any) => {
    proxyCallbackGroup[name] = callback;
    timeoutGroup[name] = setTimeout(() => {
      console.timeEnd(`fetch ${name}`);
      console.log(`fetch ${name} timeout`);

      callback({ error: new Error('要求逾時'), data: '' });
    }, 10000);

    const errorMessage: any = {
      userId: 'fetching userId error',
      accessToken: 'fetching accessToken error',
      analyticsKey: 'fetching analyticsKey error',
    };

    if (!targetProxy) {
      targetProxy = new Proxy(
        {},
        {
          set: (target: any, key: string, value: string) => {
            target[key] = value;
            clearTimeout(timeoutGroup[key]);

            console.timeEnd(`fetch ${key}`);

            if (typeof value === 'string' && value !== '' && value !== 'null') {
              console.log(`fetch ${key} success`);
              proxyCallbackGroup[key]({ error: null, data: target[key] });
            } else {
              console.log(`fetch ${key} failed`);
              proxyCallbackGroup[key]({
                error: new Error(errorMessage[key]),
                data: '',
              });
            }
            return true;
          },
        },
      );
    }
  };

  abstract fetchAccessToken(callback: fetchAccessTokenCallbackType): void;
  abstract fetchAnalyticsKey(callback: fetchAnalyticsKeyCallbackType): void;
  abstract showRefreshUserLoading(): void;
  abstract executeDeepLink(payload: executeDeepLinkPayloadType): void;
  abstract executeBoardAction(id: string, action: any): void;
  abstract closeWebView(): void;
  abstract showToast(text: string): void;
  abstract openDPassAuthSheet(
    resourceOwnerLinkId: string,
    checkCode: string,
  ): void;
  abstract logWebEvent(
    eventName: string,
    properties: Record<string, unknown>,
  ): void;
}

class AndroidBridge extends Bridge {
  toggleAutoRefresh = (enable: boolean): void => {
    window.autopass.toggleAutoRefresh(enable);
  };

  toggleWebviewLeaveDialog = (
    payload: toggleWebviewLeaveDialogPayloadType,
  ): void => {
    window.autopass.confirmDialog(JSON.stringify(payload));
  };

  fetchUserId = (callback: fetchUserIdCallbackType) => {
    console.time('fetch userId');
    const userId = window.autopass.getUserId();
    callback({ error: null, data: userId });
  };

  fetchAccessToken = (callback: fetchAccessTokenCallbackType) => {
    console.time('fetch accessToken');
    this.setProxy('accessToken', callback);
    window.autopass.requestAccessToken();
  };

  fetchAnalyticsKey = (callback: fetchAnalyticsKeyCallbackType) => {
    console.time('fetch analyticsKey');
    this.setProxy('analyticsKey', callback);
    window.autopass.requestAnalyticsKey();
  };

  showRefreshUserLoading = () => {
    window.autopass.showRefreshUserLoading();
  };

  executeDeepLink = (payload: executeDeepLinkPayloadType) => {
    window.autopass.executeDeepLink(payload.deepLink, payload.closeWebView);
  };

  executeBoardAction = (id: string, action: any) => {
    const actionObj = toSnakeCase(action);
    window.autopass.executeBoardAction(id, JSON.stringify(actionObj));
  };

  closeWebView = () => {
    window.autopass.closeWebView();
  };

  openBrowser = (url: string) => {
    window.autopass.openBrowser(url);
  };

  showToast = (text: string) => {
    window.autopass.showToast(text);
  };

  openDPassAuthSheet = (resourceOwnerLinkId: string, checkCode: string) => {
    window.autopass.openDPassAuthSheet(resourceOwnerLinkId, checkCode);
  };

  logWebEvent = (eventName: string, properties: Record<string, unknown>) =>
    window.autopass.logWebEvent(
      JSON.stringify({
        event_name: eventName,
        properties,
      }),
    );
}

class WebkitBridge extends Bridge {
  toggleAutoRefresh = (enable: boolean): void => {
    window.webkit.messageHandlers.toggleAutoRefresh.postMessage({ enable });
  };

  toggleWebviewLeaveDialog = (payload: toggleWebviewLeaveDialogPayloadType) => {
    window.webkit.messageHandlers.confirmDialog.postMessage(
      JSON.stringify(payload),
    );
  };

  fetchUserId = (callback: fetchUserIdCallbackType) => {
    console.time('fetch userId');
    this.setProxy('userId', callback);
    window.webkit.messageHandlers.getUserId.postMessage('');
  };

  fetchAccessToken = (callback: fetchAccessTokenCallbackType) => {
    console.time('fetch accessToken');
    this.setProxy('accessToken', callback);
    window.webkit.messageHandlers.requestAccessToken.postMessage('');
  };

  fetchAnalyticsKey = (callback: fetchAnalyticsKeyCallbackType) => {
    console.time('fetch analyticsKey');
    this.setProxy('analyticsKey', callback);
    window.webkit.messageHandlers.requestAnalyticsKey.postMessage('');
  };

  showRefreshUserLoading = () => {
    window.webkit.messageHandlers.showRefreshUserLoading.postMessage('');
  };

  executeDeepLink = (payload: executeDeepLinkPayloadType) => {
    window.webkit.messageHandlers.executeDeepLink.postMessage(payload);
  };

  executeBoardAction = (id: string, action: any) => {
    const actionObj = toSnakeCase(action);
    window.webkit.messageHandlers.executeBoardAction.postMessage({
      id,
      action: JSON.stringify(actionObj),
    });
  };

  closeWebView = () => {
    window.webkit.messageHandlers.closeWebView.postMessage({});
  };

  openBrowser = (url: string) => {
    window.webkit.messageHandlers.openBrowser.postMessage({ url: url });
  };

  showToast = (text: string) => {
    window.webkit.messageHandlers.showToast.postMessage({ text: text });
  };

  openDPassAuthSheet = (resourceOwnerLinkId: string, checkCode: string) => {
    window.webkit.messageHandlers.openDPassAuthSheet.postMessage({
      resourceOwnerLinkId,
      checkCode,
    });
  };

  logWebEvent = (eventName: string, properties: Record<string, unknown>) =>
    window.webkit.messageHandlers.logWebEvent.postMessage({
      event_name: eventName,
      properties,
    });
}

class UnknownJsBridge extends Bridge {
  toggleAutoRefresh = () => void 0;
  toggleWebviewLeaveDialog = () => void 0;
  fetchAccessToken = async () => void 0;
  fetchUserId = () => void 0;
  fetchAnalyticsKey = () => void 0;
  showRefreshUserLoading = () => void 0;
  executeDeepLink = () => void 0;
  executeBoardAction = () => void 0;
  closeWebView = () => void 0;
  openBrowser = () => void 0;
  showToast = () => void 0;
  openDPassAuthSheet = () => void 0;
  logWebEvent = () => void 0;
}

export class AppBridge {
  os;

  constructor() {
    this.os = this.getOS();
  }

  getOS = () => {
    try {
      const userAgent = window.navigator.userAgent.toLowerCase();
      const appInfo = window.navigator.userAgent.match(
        /Autopass\/(\d+\.\d+\.\d+)/,
      );
      const appVersion = appInfo !== null ? appInfo[1] : null;

      if (appVersion) {
        if (userAgent.match(/android/) != null) {
          console.log('this device is android.');
          return new AndroidBridge();
        }
        console.log('this device is ios.');
        return new WebkitBridge();
      } else {
        console.log('this device is desktop.');
        return new UnknownJsBridge();
      }
    } catch {
      return new UnknownJsBridge();
    }
  };

  toggleAutoRefresh = (enable: boolean) => {
    this.os.toggleAutoRefresh(enable);
  };

  toggleWebviewLeaveDialog = (payload: toggleWebviewLeaveDialogPayloadType) => {
    this.os.toggleWebviewLeaveDialog(payload);
  };

  executeDeepLink = (payload: executeDeepLinkPayloadType) => {
    this.os.executeDeepLink(payload);
  };

  executeBoardAction = (id: string, action: any) => {
    this.os.executeBoardAction(id, action);
  };

  fetchUserId = (callback: fetchUserIdCallbackType) => {
    return this.os.fetchUserId(callback);
  };

  fetchAccessToken = (callback: fetchAccessTokenCallbackType) => {
    this.os.fetchAccessToken(callback);
  };

  fetchAnalyticsKey = (callback: fetchAnalyticsKeyCallbackType) => {
    this.os.fetchAnalyticsKey(callback);
  };

  showRefreshUserLoading = () => {
    this.os.showRefreshUserLoading();
  };

  closeWebview = () => {
    this.os.closeWebView();
  };

  openBrowser = (url: string) => {
    this.os.openBrowser(url);
  };

  showToast = (text: string) => {
    this.os.showToast(text);
  };

  openDPassAuthSheet = (resourceOwnerLinkId: string, checkCode: string) => {
    this.os.openDPassAuthSheet(resourceOwnerLinkId, checkCode);
  };

  logWebEvent = (eventName: string, p: Record<string, unknown>) => {
    const properties = Object.fromEntries(
      Object.entries(p).map(([key, value]) => [key, String(value)]),
    ) satisfies Record<string, string>;

    this.os.logWebEvent(eventName, properties);
  };
}
